// İSG uzmanı portalı — ortak personel düzenleyicisinin (`usePersonnelEditor`) bağı (#29):
// taşeron personel kaydı §13.3 `PUT /contractors/{uuid}/personnel/{userUuid}`. `vehicleClient.ts`'in
// personel ikizi; EP wire gövdesini kuran TEK yer burasıdır.
//
// `tcKimlikNo` anahtarı YALNIZ `tc.kind === 'set'` iken konur (kullanıcı yetkiyle boş T.C.'ye yazdı).
// `keep`te anahtar hiç yoktur: yetkisiz anahtar `403 PERSONNEL_TC_WRITE_FORBIDDEN` döner ve maskeli
// değer asla geri gönderilmez. Kayıtlı T.C. kilitlidir → `null` (temizle) bu arayüzden ulaşılmazdır.
// Ad/soyad `null` = değişmez (kayıtlı ad boş ve alan boş bırakıldı; `PersonnelChange`) — adsız bir
// kişi ad uydurmadan pasife alınabilir.
//
// Ad küresel `SupplierUser`dadır (§13.6 "SupplierUser durumu globaldir"): kaydetmeden sonra kişi
// listesi + kişiyi gömen HER izin detayı + izin listesi + kontrol aşaması + adı canlı çözen panel
// ("Aksiyon gerekli", "Bugünkü girişler") ve güvenlik gün listesi tazelenir. Hata sonrası da aynısı
// (404 / yetki değişimi sonrası 403 / 409 → satır bayat olabilir).

import {
  type ContractorPersonnelDto,
  type PersonnelClient,
  contractorsApi,
} from '@isgportal/shared';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { contractorsKeys } from './contractorsKeys';
import { dashboardKeys } from './dashboardKeys';
import { departmentReviewKeys } from './departmentReviewKeys';
import { permitsKeys } from './permitsKeys';
import { securityKeys } from './securityKeys';

function invalidatePersonnelRecordCaches(queryClient: QueryClient, contractorUuid: string) {
  queryClient.invalidateQueries({ queryKey: contractorsKeys.personnel(contractorUuid) });
  // Pasife alınan / çıkarılan kişi izin adayı olmaktan HEMEN çıksın (aday sorgusu 60 sn taze sayılır).
  queryClient.invalidateQueries({ queryKey: permitsKeys.personnelOptions(contractorUuid) });
  queryClient.invalidateQueries({ queryKey: permitsKeys.detailAll() });
  queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
  queryClient.invalidateQueries({ queryKey: departmentReviewKeys.all() });
  // `personnelName`'i canlı çözen öbür yüzeyler: panel "Aksiyon gerekli" listesi ile "Bugünkü
  // girişler" + güvenlik gün listesi (ikisi de `useSecurityDayEntries`).
  queryClient.invalidateQueries({ queryKey: dashboardKeys.actionItemsList() });
  queryClient.invalidateQueries({ queryKey: securityKeys.entriesAll() });
}

export function usePersonnelClient(contractorUuid: string): PersonnelClient {
  const queryClient = useQueryClient();
  return useMemo<PersonnelClient>(
    () => ({
      save: (userUuid, change) =>
        contractorsApi.updatePersonnel(contractorUuid, userUuid, {
          firstName: change.firstName,
          lastName: change.lastName,
          accessLevel: change.accessLevel,
          isActive: change.isActive,
          ...(change.tc.kind === 'set' ? { tcKimlikNo: change.tc.value } : {}),
        }),
      afterSave: (updated) => {
        // Yanıt (kitle kuralıyla maskeli) listeye hemen yazılır → çekmece başlığı ve satır yeniden
        // çekmeyi beklemeden yeni adı gösterir; ardından tazeleme sunucu sırasını/durumunu getirir.
        queryClient.setQueryData<ContractorPersonnelDto[]>(
          contractorsKeys.personnel(contractorUuid),
          (prev) => prev?.map((p) => (p.uuid === updated.uuid ? updated : p)),
        );
        invalidatePersonnelRecordCaches(queryClient, contractorUuid);
      },
      afterError: () => invalidatePersonnelRecordCaches(queryClient, contractorUuid),
    }),
    [contractorUuid, queryClient],
  );
}
